// Dashboard principal — Vue d'ensemble maintenance + Actions prioritaires
// Remplace l'ancienne card "Bienvenue" qui exposait du debug info.
import { useQuery } from "@tanstack/react-query";
import { Activity, Wrench, CheckCircle, Clock, FileText, AlertTriangle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useT } from "@/lib/i18n";
import { Link } from "react-router-dom";
import { API_BASE } from "@/lib/apiBase";
import { ht } from "@/lib/ht";

interface DashboardSummary {
  interventions: {
    total: number;
    preventives: number;
    curatives: number;
    terminees: number;
    avec_da: number;
  };
  demandes_achat: {
    actives: number;
    emises: number;
  };
  agenda_en_retard: number;
}
async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const r = await fetch(`${API_BASE}${url}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
  });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json() as Promise<T>;
}

function KpiCard({
  title,
  value,
  description,
  icon: Icon,
  accent,
}: {
  title: string;
  value: number | string;
  description: string;
  icon: React.ElementType;
  accent?: "primary" | "secondary";
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${accent === "primary" ? "text-primary" : "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardContent>
    </Card>
  );
}

function ActionItem({
  count,
  label,
  link,
  variant = "default",
}: {
  count: number;
  label: string;
  link: string;
  variant?: "default" | "warning" | "danger" | "success";
}) {
  const variantStyles: Record<string, string> = {
    default: "hover:bg-accent",
    warning: "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-500/15 border border-amber-500/40 dark:hover:bg-amber-950/50 border-amber-200 dark:border-amber-800",
    danger: "bg-red-50 dark:bg-red-950/30 hover:bg-destructive/15 border border-destructive/40 dark:hover:bg-red-950/50 border-red-200 dark:border-red-800",
    success: "bg-green-50 dark:bg-green-950/30 hover:bg-emerald-500/15 border border-emerald-500/40 dark:hover:bg-green-950/50 border-green-200 dark:border-green-800",
  };
  const textStyles: Record<string, string> = {
    default: "text-foreground",
    warning: "text-amber-900 dark:text-amber-100",
    danger: "text-red-900 dark:text-red-100",
    success: "text-green-900 dark:text-green-100",
  };
  const badgeStyles: Record<string, string> = {
    default: "bg-muted text-muted-foreground",
    warning: "bg-amber-200 dark:bg-amber-900 text-amber-900 dark:text-amber-100",
    danger: "bg-red-200 dark:bg-red-900 text-red-900 dark:text-red-100",
    success: "bg-green-200 dark:bg-green-900 text-green-900 dark:text-green-100",
  };

  return (
    <Link
      to={link}
      className={`flex items-center justify-between rounded-md border p-3 transition-colors ${variantStyles[variant]}`}
    >
      <div className="flex items-center gap-3">
        <span
          className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold ${badgeStyles[variant]}`}
        >
          {count}
        </span>
        <span className={`text-sm font-medium ${textStyles[variant]}`}>{label}</span>
      </div>
      <ArrowRight className={`h-4 w-4 ${textStyles[variant]}`} />
    </Link>
  );
}

function DashboardContent() {
  const t = useT();
  const { data: kpis, isLoading } = useQuery<DashboardSummary>({
    queryKey: ["dashboard-kpis"],
    queryFn: () => fetchJson<DashboardSummary>("/v1/dashboard/summary"),
  });

  // Demandes d'achat en attente (statut "emise")
  const { data: daEnAttente } = useQuery({
    queryKey: ["da-en-attente"],
    queryFn: () =>
      fetchJson<Array<{ id: number; code: string; statut: string }>>("/v1/demandes-achat"),
    select: (data) => data.filter((d) => d.statut === "emise"),
  });

  // Engins en retard (cycle preventif)
  const { data: enginsEnRetard } = useQuery({
    queryKey: ["engins-en-retard"],
    queryFn: () =>
      fetchJson<Array<{ engin_id: number; engin_code: string; statut: string }>>(
        "/v1/interventions/cycle-preventif/all"
      ),
    select: (data) => data.filter((e) => e.statut === "En retard"),
  });

  // Interventions en cours
  const { data: interventionsEnCours } = useQuery({
    queryKey: ["interventions-en-cours"],
    queryFn: () =>
      fetchJson<Array<{ id: number; intervention_code: string; statut: string }>>(
        "/v1/interventions?statut=in_progress"
      ),
  });

  if (isLoading || !kpis) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  const daCount = daEnAttente?.length ?? 0;
  const retardCount = enginsEnRetard?.length ?? 0;
  const enCoursCount = interventionsEnCours?.length ?? 0;
  const hasActions = daCount + retardCount + enCoursCount > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("dashboard.title")}</h1>
        <p className="text-muted-foreground">
          {t("dashboard.subtitle")}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard
          title={t("dashboard.kpi.total")}
          value={kpis.interventions.total}
          description={t("dashboard.kpi.of_total")}
          icon={Activity}
          accent="primary"
        />
        <KpiCard
          title={t("dashboard.kpi.preventives")}
          value={kpis.interventions.preventives}
          description={`${kpis.interventions.total > 0 ? Math.round((kpis.interventions.preventives / kpis.interventions.total) * 100) : 0}% ${t("dashboard.kpi.of_total")}`}
          icon={Wrench}
        />
        <KpiCard
          title={t("dashboard.kpi.curatives")}
          value={kpis.interventions.curatives}
          description={`${kpis.interventions.total > 0 ? Math.round((kpis.interventions.curatives / kpis.interventions.total) * 100) : 0}% ${t("dashboard.kpi.of_total")}`}
          icon={Clock}
        />
        <KpiCard
          title={t("dashboard.kpi.terminees")}
          value={kpis.interventions.terminees}
          description={`${kpis.interventions.total > 0 ? Math.round((kpis.interventions.terminees / kpis.interventions.total) * 100) : 0}% ${t("dashboard.kpi.of_total")}`}
          icon={CheckCircle}
        />
        <KpiCard
          title={t("dashboard.kpi.avec_da")}
          value={kpis.interventions.avec_da}
          description={t("dashboard.kpi.da_desc")}
          icon={FileText}
        />
        <KpiCard
          title={t("dashboard.kpi.en_retard")}
          value={kpis.agenda_en_retard}
          description={t("dashboard.kpi.retard_desc")}
          icon={AlertTriangle}
          accent={kpis.agenda_en_retard > 0 ? "secondary" : "primary"}
        />
      </div>

      {/* Actions prioritaires — aide l'utilisateur à savoir QUOI FAIRE maintenant */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Actions prioritaires")}</CardTitle>
          <CardDescription>
            {hasActions
              ? t("dashboard.tasks_title")
              : t("dashboard.no_action")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!hasActions ? (
            <div className="flex flex-col items-center justify-center gap-2 py-6 text-muted-foreground">
              <CheckCircle className="h-10 w-10 text-green-500" />
              <p className="text-sm">{ht("Aucune intervention en attente de validation ou en retard.")}</p>
            </div>
          ) : (
            <div className="space-y-2">
              {daCount > 0 && (
                <ActionItem
                  count={daCount}
                  label={t("dashboard.da_to_validate")}
                  link="/demandes-achat"
                  variant={daCount >= 3 ? "danger" : "warning"}
                />
              )}
              {retardCount > 0 && (
                <ActionItem
                  count={retardCount}
                  label={t("dashboard.engins_late")}
                  link="/agenda-maintenance"
                  variant="danger"
                />
              )}
              {enCoursCount > 0 && (
                <ActionItem
                  count={enCoursCount}
                  label={t("dashboard.interventions_in_progress")}
                  link="/maintenance"
                  variant="default"
                />
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Indicateurs business (vue d'ensemble) */}
      <Card>
        <CardHeader>
          <CardTitle>{ht("Indicateurs clés")}</CardTitle>
          <CardDescription>
            {t("dashboard.indicators.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <div className="text-xs text-muted-foreground">{ht("Taux de complétion")}</div>
              <div className="text-2xl font-bold">
                {kpis.interventions.total > 0
                  ? Math.round((kpis.interventions.terminees / kpis.interventions.total) * 100)
                  : 0}
                %
              </div>
              <div className="text-xs text-muted-foreground">
                {kpis.interventions.terminees} / {kpis.interventions.total} interventions
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{ht("Ratio préventif")}</div>
              <div className="text-2xl font-bold">
                {kpis.interventions.total > 0
                  ? Math.round((kpis.interventions.preventives / kpis.interventions.total) * 100)
                  : 0}
                %
              </div>
              <div className="text-xs text-muted-foreground">{ht("préventif vs curatif")}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{ht("DA actives")}</div>
              <div className="text-2xl font-bold">{kpis.demandes_achat.actives}</div>
              <div className="text-xs text-muted-foreground">{ht("non annulées")}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{ht("Avec DA")}</div>
              <div className="text-2xl font-bold">{kpis.interventions.avec_da}</div>
              <div className="text-xs text-muted-foreground">{ht("interventions consommables")}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardPage() {
  return <DashboardContent />;
}
